import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, TextField, InputAdornment, Avatar, Chip, IconButton,
  Tooltip, Typography, Paper, Skeleton, TableSortLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HEAD_COLS = [
  { id: 'name',      label: 'Company',   minWidth: 240 },
  { id: 'btwNumber', label: 'BTW / VAT', minWidth: 130 },
  { id: 'city',      label: 'Location',  minWidth: 140 },
  { id: 'users',     label: 'Users',     align: 'center', minWidth: 80 },
  { id: 'website',   label: 'Website',   minWidth: 160 },
];

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';
}

const CompanyTable = ({ companies = [], loading = false, onOpen, onDelete }) => {
  const navigate = useNavigate();
  const [search, setSearch]             = useState('');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [orderBy, setOrderBy]           = useState('name');
  const [order, setOrder]               = useState('asc');

  const handleEditClick = (e, company) => {
    e.stopPropagation();
    navigate(`/admin-user-management/company/${company.id}`);
  };

  const handleViewClick = (e, company) => {
    e.stopPropagation();
    navigate(`/admin-user-management/company/${company.id}`);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? companies.filter(c =>
          c.name.toLowerCase().includes(q) ||
          (c.btwNumber  || '').toLowerCase().includes(q) ||
          (c.city       || '').toLowerCase().includes(q) ||
          (c.country    || '').toLowerCase().includes(q)
        )
      : companies;

    return [...rows].sort((a, b) => {
      const va = orderBy === 'users' ? a.users.length : (a[orderBy] ?? '');
      const vb = orderBy === 'users' ? b.users.length : (b[orderBy] ?? '');
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return order === 'asc' ? cmp : -cmp;
    });
  }, [companies, search, orderBy, order]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
    setPage(0);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{
        px: 2.5, py: 1.5, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: '1px solid',
        borderColor: 'divider', bgcolor: 'white',
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {loading ? 'Loading…' : `${filtered.length} compan${filtered.length !== 1 ? 'ies' : 'y'}`}
        </Typography>
        <TextField
          size="small" placeholder="Search by name, city, BTW…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.84rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {HEAD_COLS.map(col => (
                <TableCell key={col.id} align={col.align}
                  sx={{
                    fontWeight: 700, fontSize: '0.76rem', bgcolor: '#f7f7f7',
                    minWidth: col.minWidth, textTransform: 'uppercase',
                    letterSpacing: '0.4px', color: 'text.secondary',
                  }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{
                fontWeight: 700, fontSize: '0.76rem', bgcolor: '#f7f7f7',
                textTransform: 'uppercase', letterSpacing: '0.4px',
                color: 'text.secondary', width: 130,
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((__, j) => (
                    <TableCell key={j}><Skeleton height={18} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 8, textAlign: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 44, color: 'action.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {search ? 'No companies match your search' : 'No companies found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paged.map(company => (
              <TableRow
                key={company.name} hover
                onClick={() => navigate(`/admin-user-management/company/${company.id}`)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(102,126,234,0.04)' },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                {/* Company name + avatar */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{
                      width: 36, height: 36, fontSize: '0.7rem', fontWeight: 800,
                      borderRadius: 1.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}>
                      {getInitials(company.name)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.3 }}>
                        {company.name}
                      </Typography>
                      {company.companyPeppolId && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                          Peppol: {company.companyPeppolId}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                {/* BTW / VAT */}
                <TableCell>
                  <Typography sx={{ fontSize: '0.84rem' }}>{company.btwNumber || '—'}</Typography>
                </TableCell>

                {/* Location */}
                <TableCell>
                  <Typography sx={{ fontSize: '0.84rem' }}>
                    {[company.city, company.country].filter(Boolean).join(', ') || '—'}
                  </Typography>
                </TableCell>

                {/* Users count */}
                <TableCell align="center">
                  <Chip
                    icon={<PeopleIcon sx={{ fontSize: '13px !important' }} />}
                    label={company.users.length}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: 'rgba(102,126,234,0.1)', color: '#667eea', fontSize: '0.72rem' }}
                  />
                </TableCell>

                {/* Website */}
                <TableCell>
                  <Typography sx={{
                    fontSize: '0.84rem', maxWidth: 180,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {company.website || '—'}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right" onClick={e => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="View Details">
                    <IconButton size="small" sx={{ color: '#667eea' }} onClick={(e) => handleViewClick(e, company)}>
                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Company">
                    <IconButton size="small" sx={{ color: '#ff9800' }} onClick={(e) => handleEditClick(e, company)}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Company">
                    <IconButton size="small" sx={{ color: '#f44336' }} onClick={() => onDelete(company)}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
      />
    </Paper>
  );
};

export default React.memo(CompanyTable);
